import { CLIENT_PAGES } from '@/components/client/constants/pages'
import type { FormDataSections } from '@/flows/types'
import {
  addressInfoValidationRules,
  personalInfoValidationRules,
} from '@/utilities/formValidations'
import { createEmptyAddressInfo, createEmptyPersonalInfo } from '@/flows/utils/formDataDefaults'

export const CLIENT_KIT_REGISTRATION_STEP_KEYS = [
  'kit-validation',
  'consent',
  'patient-info',
  'address-info',
  'review',
  'success',
] as const

export type ClientKitRegistrationStepKey = (typeof CLIENT_KIT_REGISTRATION_STEP_KEYS)[number]

export type ClientKitRegistrationStep = {
  key: ClientKitRegistrationStepKey
  name: string
}

export const CLIENT_KIT_REGISTRATION_SUCCESS_STEP = {
  key: 'success',
  name: 'Success',
} as const satisfies ClientKitRegistrationStep

export const CLIENT_KIT_REGISTRATION_FLOW = {
  id: 'client-kit-registration',
  name: 'Kit Registration',
  baseUrl: CLIENT_PAGES.kitRegistration,
  steps: [
    {
      key: 'kit-validation',
      name: 'Kit ID',
    },
    {
      key: 'consent',
      name: 'Consent Agreements',
    },
    {
      key: 'patient-info',
      name: 'Patient Information',
    },
    {
      key: 'address-info',
      name: 'Address Information',
    },
    {
      key: 'review',
      name: 'Review & Submit',
    },
  ] satisfies ClientKitRegistrationStep[],
} as const

export const CLIENT_KIT_REGISTRATION_FIRST_STEP_KEY =
  CLIENT_KIT_REGISTRATION_FLOW.steps[0]?.key ?? 'kit-validation'

export const CLIENT_KIT_REGISTRATION_FIRST_STEP_PATH = CLIENT_KIT_REGISTRATION_FLOW.baseUrl

export type ClientKitValidationInfo = {
  kitId: string
  validationStatus: 'valid'
  validatedAt: string
  sku?: string
  kitStateIsNy?: boolean
  skipRegistration?: boolean
}

export type ClientKitRegistrationPatientInfo = NonNullable<FormDataSections['personalInfo']>
export type ClientKitRegistrationAddressInfo = NonNullable<FormDataSections['addressInfo']>

export type ClientKitRegistrationConsentInfo = {
  atHomeConsentAgreed: boolean
  hipaaConsentAgreed: boolean
  consentedAt?: string
}

export type ClientKitRegistrationFormData = {
  kitInfo?: ClientKitValidationInfo
  patientInfo: ClientKitRegistrationPatientInfo
  addressInfo: ClientKitRegistrationAddressInfo
  consentInfo: ClientKitRegistrationConsentInfo
}

export function isClientKitRegistrationStepKey(
  stepKey: string,
): stepKey is ClientKitRegistrationStepKey {
  return CLIENT_KIT_REGISTRATION_STEP_KEYS.includes(stepKey as ClientKitRegistrationStepKey)
}

export function getClientKitRegistrationStep(stepKey: ClientKitRegistrationStepKey) {
  if (stepKey === 'success') {
    return CLIENT_KIT_REGISTRATION_SUCCESS_STEP
  }
  return CLIENT_KIT_REGISTRATION_FLOW.steps.find((step) => step.key === stepKey)
}

export function getClientKitRegistrationStepIndex(stepKey: ClientKitRegistrationStepKey) {
  if (stepKey === 'success') {
    return CLIENT_KIT_REGISTRATION_FLOW.steps.length
  }
  return CLIENT_KIT_REGISTRATION_FLOW.steps.findIndex((step) => step.key === stepKey)
}

export function getNextClientKitRegistrationStepKey(
  stepKey: ClientKitRegistrationStepKey,
): ClientKitRegistrationStepKey | null {
  if (stepKey === 'review') {
    return 'success'
  }
  if (stepKey === 'success') {
    return null
  }

  const nextStep =
    CLIENT_KIT_REGISTRATION_FLOW.steps[getClientKitRegistrationStepIndex(stepKey) + 1]
  return nextStep?.key ?? null
}

export function getPreviousClientKitRegistrationStepKey(
  stepKey: ClientKitRegistrationStepKey,
): ClientKitRegistrationStepKey | null {
  if (stepKey === 'success') {
    return 'review'
  }

  const previousStep =
    CLIENT_KIT_REGISTRATION_FLOW.steps[getClientKitRegistrationStepIndex(stepKey) - 1]
  return previousStep?.key ?? null
}

export function createEmptyClientKitRegistrationFormData(): ClientKitRegistrationFormData {
  return {
    patientInfo: createEmptyPersonalInfo(),
    addressInfo: createEmptyAddressInfo(),
    consentInfo: {
      atHomeConsentAgreed: false,
      hipaaConsentAgreed: false,
    },
  }
}

export function isClientKitRegistrationConsentComplete(
  consentInfo: ClientKitRegistrationConsentInfo,
): boolean {
  return consentInfo.atHomeConsentAgreed && consentInfo.hipaaConsentAgreed
}

export const clientKitRegistrationPatientInfoValidationRules =
  personalInfoValidationRules<ClientKitRegistrationPatientInfo>()

export const clientKitRegistrationAddressInfoValidationRules =
  addressInfoValidationRules<ClientKitRegistrationAddressInfo>()
